import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { cartPerformance } from '@theme/performance';

/**
 * Shipping protection, ported from the live Neto site.
 *
 * All of the pricing logic lives in `snippets/shipping-protection.liquid`, which
 * re-renders with the cart sections on every cart change. This module only reads
 * the state Liquid published and moves the cart towards it:
 *
 * - no protection in the cart, and the customer has not opted out -> add the tier variant
 * - protection in the cart at the wrong tier -> swap it in a single request
 * - protection in the cart but nothing left to protect -> take it out
 *
 * Opting out is remembered for the session, matching the live site's
 * `removedInsurance` flag: once the customer removes protection it is not added
 * back until they ask for it or open a new session.
 */

const OPT_OUT_KEY = 'orl:shipping-protection-opted-out';

/** Guards against overlapping reconciles while a cart request is in flight. */
let inFlight = false;

/** Pending debounce timer, so a burst of cart events reconciles once. */
let scheduled = null;

/**
 * Consecutive reconciles that resulted in a cart write. The ladder is measured
 * against the goods subtotal, which protection itself never changes, so this
 * should never climb - it is a backstop against a mis-set ladder looping the cart.
 */
let consecutiveWrites = 0;
const MAX_CONSECUTIVE_WRITES = 4;

/**
 * Reads the session opt-out flag.
 * @returns {boolean}
 */
function hasOptedOut() {
  try {
    return sessionStorage.getItem(OPT_OUT_KEY) === '1';
  } catch (error) {
    return false;
  }
}

/**
 * Writes the session opt-out flag.
 * @param {boolean} value
 */
function setOptedOut(value) {
  try {
    if (value) {
      sessionStorage.setItem(OPT_OUT_KEY, '1');
    } else {
      sessionStorage.removeItem(OPT_OUT_KEY);
    }
  } catch (error) {
    // Private mode or blocked storage: protection still works, it just stops
    // remembering the opt-out beyond the current page.
  }
}

/**
 * The cart sections currently on the page, so one request refreshes the cart
 * page and the drawer together.
 * @returns {string[]}
 */
function cartSectionIds() {
  const ids = new Set();

  for (const element of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (!(element instanceof HTMLElement)) continue;
    const id = element.dataset.sectionId;
    if (id) ids.add(id);
  }

  return Array.from(ids);
}

/**
 * Applies a set of cart quantity updates keyed by variant id, then tells the
 * theme to re-render from the sections that came back with the response.
 *
 * @param {Record<string, number>} updates
 * @param {Element} source - The element the cart update event is dispatched from.
 * @returns {Promise<void>}
 */
async function applyCartUpdates(updates, source) {
  const body = JSON.stringify({
    updates,
    sections: cartSectionIds().join(','),
    sections_url: window.location.pathname,
  });

  const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
  const cart = await response.json();

  if (cart.status || cart.errors) {
    throw new Error(cart.description || cart.message || 'Cart update failed');
  }

  source.dispatchEvent(
    new CartUpdateEvent(cart, 'shipping-protection', {
      source: 'shipping-protection',
      itemCount: cart.item_count,
      sections: cart.sections,
    })
  );
}

/**
 * The state Liquid published for the current cart, read from the first
 * protection element on the page. The cart page renders two (the page and the
 * drawer) and they always agree, so one read is enough.
 *
 * @returns {{ element: Element, eligible: boolean, target: string, current: string } | null}
 */
function readState() {
  const element = document.querySelector('shipping-protection[data-enabled="true"]');
  if (!(element instanceof HTMLElement)) return null;

  return {
    element,
    eligible: element.dataset.eligible === 'true',
    target: element.dataset.targetVariantId || '',
    current: element.dataset.currentVariantId || '',
  };
}

/**
 * Works out the cart write, if any, that moves the cart to the state Liquid asked for.
 *
 * @param {{ eligible: boolean, target: string, current: string }} state
 * @param {boolean} optedOut
 * @returns {Record<string, number> | null}
 */
function resolveUpdates(state, optedOut) {
  const { eligible, target, current } = state;

  if (!eligible) {
    return current ? { [current]: 0 } : null;
  }

  if (!target) return null;

  if (current) {
    return current === target ? null : { [current]: 0, [target]: 1 };
  }

  return optedOut ? null : { [target]: 1 };
}

/**
 * Brings the cart in line with the rendered tier. Safe to call repeatedly:
 * it is a no-op once the cart already holds the right variant.
 */
async function reconcile() {
  if (inFlight) return;

  const state = readState();
  if (!state) {
    consecutiveWrites = 0;
    return;
  }

  const updates = resolveUpdates(state, hasOptedOut());
  if (!updates) {
    consecutiveWrites = 0;
    return;
  }

  if (consecutiveWrites >= MAX_CONSECUTIVE_WRITES) {
    console.warn('[shipping-protection] stopped reconciling after repeated cart writes');
    return;
  }

  inFlight = true;
  consecutiveWrites += 1;

  const marker = cartPerformance.createStartingMarker('shipping-protection:reconcile');

  try {
    await applyCartUpdates(updates, state.element);
  } catch (error) {
    console.error('[shipping-protection]', error);
    consecutiveWrites = MAX_CONSECUTIVE_WRITES;
  } finally {
    inFlight = false;
    cartPerformance.measureFromMarker(marker);
  }
}

/**
 * Queues a reconcile. Cart events fire before the sections morph in, so this
 * waits a beat for the freshly rendered state rather than reading the old one.
 */
function scheduleReconcile() {
  if (scheduled) clearTimeout(scheduled);
  scheduled = setTimeout(() => {
    scheduled = null;
    reconcile();
  }, 50);
}

/**
 * Shipping protection block rendered in the cart page summary and the cart drawer.
 * @extends {Component<{ dialog: HTMLDialogElement }>}
 */
class ShippingProtection extends Component {
  connectedCallback() {
    super.connectedCallback();
    scheduleReconcile();
  }

  /** Adds protection back after the customer opted out. */
  addProtection = () => {
    setOptedOut(false);
    consecutiveWrites = 0;
    scheduleReconcile();
  };

  /** Opens the confirmation dialog, matching the live site's remove modal. */
  confirmRemove = () => {
    const { dialog } = this.refs;
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    dialog.showModal();
  };

  /** Closes the confirmation dialog. */
  closeDialog = () => {
    const { dialog } = this.refs;
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
  };

  /** Removes protection and remembers the opt-out for the session. */
  removeProtection = async () => {
    this.closeDialog();

    const current = this.dataset.currentVariantId;
    if (!current) return;

    setOptedOut(true);
    consecutiveWrites = 0;

    if (inFlight) return;
    inFlight = true;

    try {
      await applyCartUpdates({ [current]: 0 }, this);
    } catch (error) {
      console.error('[shipping-protection]', error);
    } finally {
      inFlight = false;
    }
  };
}

if (!customElements.get('shipping-protection')) {
  customElements.define('shipping-protection', ShippingProtection);
}

/**
 * The protection line in the cart list sits outside this component, so its
 * remove button is wired up here and routed to the block's confirmation dialog.
 */
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest('[data-shipping-protection-remove]');
  if (!trigger) return;

  event.preventDefault();

  const block = trigger.closest('cart-items-component')?.querySelector('shipping-protection');
  if (block instanceof ShippingProtection) block.confirmRemove();
});

document.addEventListener(ThemeEvents.cartUpdate, scheduleReconcile);
